import React from "react";
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
} from "@mui/material";
import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import PageContainer from "../layout/PageContainer";
import useCuisines from "../../hooks/useCuisines";

const blank = {
    legacy_id: "",
    name: "",
    label_de: "",
    label_en: "",
    label_fr: "",
    label_it: "",
    sort_order: 0,
    is_active: true,
};

function cleanOpt(v) {
    const s = (v || "").trim();
    return s ? s : null;
}

function getCellValue(row, key) {
    if (key === "active") return row.is_active ? 1 : 0;
    return row[key] ?? "";
}

function descendingComparator(a, b, orderBy) {
    const va = getCellValue(a, orderBy);
    const vb = getCellValue(b, orderBy);
    const na = typeof va === "number" ? va : Number(va);
    const nb = typeof vb === "number" ? vb : Number(vb);
    if (Number.isFinite(na) && Number.isFinite(nb)) return nb - na;
    return String(vb ?? "").localeCompare(String(va ?? ""), "de", { sensitivity: "base" });
}

function getComparator(order, orderBy) {
    return order === "desc"
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    return [...array]
        .map((el, idx) => [el, idx])
        .sort((a, b) => comparator(a[0], b[0]) || a[1] - b[1])
        .map((el) => el[0]);
}

export default function CuisinesPage() {
    const {
        cuisines,
        loading,
        error,
        fetchCuisines,
        createCuisine,
        updateCuisine,
        deleteCuisine,
    } = useCuisines();

    const [order, setOrder] = React.useState("asc");
    const [orderBy, setOrderBy] = React.useState("sort_order");
    const [search, setSearch] = React.useState("");
    const [open, setOpen] = React.useState(false);
    const [form, setForm] = React.useState(blank);
    const [editing, setEditing] = React.useState(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [submitError, setSubmitError] = React.useState("");
    const [deleteTarget, setDeleteTarget] = React.useState(null);
    const [deleteError, setDeleteError] = React.useState("");
    const [deleting, setDeleting] = React.useState(false);

    const filteredRows = React.useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return cuisines || [];
        return (cuisines || []).filter((cuisine) => (
            String(cuisine.legacy_id || "").includes(q) ||
            (cuisine.name || "").toLowerCase().includes(q) ||
            (cuisine.label_de || "").toLowerCase().includes(q)
        ));
    }, [cuisines, search]);

    const rows = React.useMemo(
        () => stableSort(filteredRows, getComparator(order, orderBy)),
        [filteredRows, order, orderBy]
    );

    const columns = [
        { id: "legacy_id", label: "Alte ID" },
        { id: "label_de", label: "Label (DE)" },
        { id: "active", label: "Aktiv" },
        { id: "sort_order", label: "Sortierung" },
    ];

    const openCreate = () => {
        setEditing(null);
        setForm(blank);
        setSubmitError("");
        setOpen(true);
    };

    const openEdit = (cuisine) => {
        setEditing(cuisine);
        setForm({
            ...blank,
            ...cuisine,
            legacy_id: cuisine.legacy_id ?? "",
            sort_order: cuisine.sort_order ?? 0,
            is_active: cuisine.is_active !== undefined ? !!cuisine.is_active : true,
        });
        setSubmitError("");
        setOpen(true);
    };

    const handleSave = async () => {
        const name = (form.name || "").trim();
        const label_de = (form.label_de || "").trim();
        if (!name) {
            setSubmitError("Key/Name ist Pflichtfeld.");
            return;
        }
        if (!label_de) {
            setSubmitError("Label (DE) ist Pflichtfeld.");
            return;
        }

        const payload = {
            legacy_id: form.legacy_id === "" ? null : Number(form.legacy_id),
            name,
            label_de,
            label_en: cleanOpt(form.label_en),
            label_fr: cleanOpt(form.label_fr),
            label_it: cleanOpt(form.label_it),
            sort_order: Number(form.sort_order || 0),
            is_active: !!form.is_active,
        };

        setSubmitting(true);
        setSubmitError("");
        try {
            if (editing) await updateCuisine(editing.id, payload);
            else await createCuisine(payload);
            setOpen(false);
        } catch (e) {
            setSubmitError(e.message || "Speichern fehlgeschlagen.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PageContainer
            title="Küchen"
            actions={
                <Box sx={{ display: "flex", gap: 1 }}>
                    <Button variant="outlined" onClick={fetchCuisines} disabled={loading}>
                        Aktualisieren
                    </Button>
                    <Button variant="contained" onClick={openCreate}>
                        Neue Küche
                    </Button>
                </Box>
            }
        >
            {error && <Alert severity="error">{error}</Alert>}

            <Box sx={{ mb: 2 }}>
                <TextField
                    fullWidth
                    size="small"
                    label="Küchen suchen"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: "70vh", overflow: "auto" }}>
                {loading ? (
                    <Box sx={{ py: 6, textAlign: "center" }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                {columns.map((c) => (
                                    <TableCell key={c.id}>
                                        <TableSortLabel
                                            active={orderBy === c.id}
                                            direction={order}
                                            onClick={() => {
                                                const isAsc = orderBy === c.id && order === "asc";
                                                setOrder(isAsc ? "desc" : "asc");
                                                setOrderBy(c.id);
                                            }}
                                        >
                                            {c.label}
                                        </TableSortLabel>
                                    </TableCell>
                                ))}
                                <TableCell align="right">Aktionen</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((cuisine) => (
                                <TableRow key={cuisine.id} hover>
                                    <TableCell>{cuisine.legacy_id ?? "-"}</TableCell>
                                    <TableCell>{cuisine.label_de || "-"}</TableCell>
                                    <TableCell>{cuisine.is_active ? "Ja" : "Nein"}</TableCell>
                                    <TableCell>{cuisine.sort_order ?? 0}</TableCell>
                                    <TableCell align="right">
                                        <IconButton onClick={() => openEdit(cuisine)}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            onClick={() => {
                                                setDeleteTarget(cuisine);
                                                setDeleteError("");
                                            }}
                                        >
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </TableContainer>

            <Dialog open={open} onClose={() => !submitting && setOpen(false)} fullWidth maxWidth="md">
                <DialogTitle>{editing ? "Küche bearbeiten" : "Neue Küche anlegen"}</DialogTitle>
                <DialogContent dividers>
                    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
                        <TextField label="Alte ID" type="number" value={form.legacy_id} onChange={(e) => setForm((f) => ({ ...f, legacy_id: e.target.value }))} />
                        <TextField label="Key/Name* (technisch)" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
                        <TextField label="Label (DE)*" value={form.label_de} onChange={(e) => setForm((f) => ({ ...f, label_de: e.target.value }))} />
                        <TextField label="Label (EN)" value={form.label_en || ""} onChange={(e) => setForm((f) => ({ ...f, label_en: e.target.value }))} />
                        <TextField label="Label (FR)" value={form.label_fr || ""} onChange={(e) => setForm((f) => ({ ...f, label_fr: e.target.value }))} />
                        <TextField label="Label (IT)" value={form.label_it || ""} onChange={(e) => setForm((f) => ({ ...f, label_it: e.target.value }))} />
                        <TextField label="Sortierung" type="number" value={form.sort_order ?? 0} onChange={(e) => setForm((f) => ({ ...f, sort_order: e.target.value }))} />
                        <FormControlLabel
                            control={<Checkbox checked={!!form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />}
                            label="Aktiv"
                        />
                    </Box>
                    {submitError && <Alert severity="error" sx={{ mt: 2 }}>{submitError}</Alert>}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)} disabled={submitting}>Abbrechen</Button>
                    <Button variant="contained" onClick={handleSave} disabled={submitting}>
                        {submitting ? "Speichern..." : "Speichern"}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} fullWidth maxWidth="xs">
                <DialogTitle>Küche löschen</DialogTitle>
                <DialogContent dividers>
                    {deleteError ? (
                        <Alert severity="error">{deleteError}</Alert>
                    ) : (
                        <Alert severity="warning">
                            Willst du <b>{deleteTarget?.label_de || deleteTarget?.name}</b> wirklich löschen?
                            Zuordnungen zu Firmen werden automatisch entfernt.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Abbrechen</Button>
                    <Button
                        color="error"
                        variant="contained"
                        disabled={deleting}
                        onClick={async () => {
                            if (!deleteTarget) return;
                            setDeleting(true);
                            setDeleteError("");
                            try {
                                await deleteCuisine(deleteTarget.id);
                                setDeleteTarget(null);
                            } catch (e) {
                                setDeleteError(e.message || "Löschen fehlgeschlagen.");
                            } finally {
                                setDeleting(false);
                            }
                        }}
                    >
                        {deleting ? "Löschen..." : "Löschen"}
                    </Button>
                </DialogActions>
            </Dialog>
        </PageContainer>
    );
}
